import { useEffect } from 'react';

import type { ForgeUIContentBylineItemExtensionType } from '@confluence/forge-ui/entry-points/types.ts';

import { recordNonContentAppKeys } from '@confluence/browser-metrics/entry-points/NonContentAppKeys.ts';
import { fg } from '@confluence/feature-gating';
import { FORGE_MODULE_BYLINE } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { useExtensionList } from '@confluence/forge-ui/entry-points/useExtensionList.ts';
import { SYSTEM_CONTENT_BYLINE } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import type { useWebItemLocationWebItemType as WebItem } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

// Hook to grab byline app info for live pages
export const useByLineApps = (
	contentId: string,
): {
	hasApps: boolean;
	connectItems: WebItem[];
	loadingConnectItems: boolean;
	forgeItems: ForgeUIContentBylineItemExtensionType[];
	loadingForgeItems: boolean;
	forgeError: Error | undefined;
} => {
	const { webItems: connectItems, loading: loadingConnectItems } = useWebItemLocation({
		contentId,
		location: SYSTEM_CONTENT_BYLINE,
	});

	const {
		extensions: forgeItems,
		loading: loadingForgeItems,
		error: forgeError,
	} = useExtensionList({
		moduleType: FORGE_MODULE_BYLINE,
	});

	// Instrument the presence of non-content 3P byline apps (connect + forge) so
	// their keys are folded into the page-load `connectAppKeys` telemetry.
	useEffect(() => {
		if (fg('cc_record_non_content_macros')) {
			recordNonContentAppKeys('byline', [
				...(connectItems?.map((item) => item.completeKey) ?? []),
				...(forgeItems?.map((item) => item.id) ?? []),
			]);
		}
	}, [connectItems, forgeItems]);

	return {
		hasApps: connectItems?.length > 0 || forgeItems?.length > 0,
		connectItems,
		loadingConnectItems,
		forgeItems,
		loadingForgeItems,
		forgeError,
	};
};
